import { createContext } from 'react';

import type { AdminUser, TokenPayload } from './api';
import type { Session, SessionStatus } from './session';

/**
 * The shape of an admin session, and the context that carries it.
 *
 * Separate from `AuthProvider` because a module that exports both a component
 * and a context cannot be hot-reloaded — every edit to the provider would
 * otherwise remount the tree and take the editor's unsaved work with it, which
 * is precisely what §6.7 exists to prevent.
 *
 * Two things make this more than a token holder. The first is that
 * docs/04-API-SPEC.md §5 has three token kinds, only one of which may reach the
 * dashboard, so the *status* travels with the token and the router reads it.
 * The second is docs/05-ADMIN-DASHBOARD.md §6.7: when a session expires the
 * dashboard must show a re-auth modal that preserves unsaved form state, which
 * means a dead token can never cause a navigation — the tree stays mounted and
 * a modal goes over it.
 */
export interface AuthContextValue {
  session: Session | null;
  user: AdminUser | null;
  /** True while the stored token is being exchanged for a user on first load. */
  isRestoring: boolean;
  /** §6.7 — set by any 401 from an authenticated request. */
  needsReauth: boolean;

  signIn: (credentials: { email: string; password: string }) => Promise<SessionStatus>;
  completeChallenge: (payload: { code?: string; recovery_code?: string }) => Promise<void>;
  adoptSession: (payload: TokenPayload, user?: AdminUser) => void;
  signOut: () => Promise<void>;
  /** Re-auth succeeded; drop the modal and let the queries retry. */
  resumeSession: () => void;

  /**
   * Replaces the signed-in user after they edit their own profile.
   *
   * The header draws its name and picture from here, so without it an editor
   * would save a new photograph and go on looking at the old one until the
   * next reload — which reads as a save that did not work.
   */
  updateUser: (user: AdminUser) => void;

  /**
   * `permissions` is flattened by the server. This hides controls the user
   * cannot use (§3); it never decides anything — the Policy on the endpoint
   * does.
   */
  can: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
}

export const AuthContext = createContext<AuthContextValue | null>(null);
